import * as PDFJS from 'pdfjs-dist';
import { Buffer } from 'buffer';

export const PDF_ZERO_BYTE_ERROR = '無効なファイル（0バイト）のため登録できません。';
export const PDF_PASSWORD_PROTECTED_ERROR = 'このPDFはパスワードで保護されています。ご確認お願いします。';
export const PDF_CORRUPTED_ERROR = 'ファイルが破損しているため登録できません。';
export const PDFJS_EMPTY_FILE_ERROR = 'The PDF file is empty, i.e. its size is zero bytes.';

export default {
  /**
   * ファイルをbase64文字列に変換します。
   *
   * @param file - 変換するファイル
   * @returns - base64文字列を返すプロミス
   */
  toBase64: (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      try {
        const r = reader.result as string;
        resolve(r.split(',')[1]);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
  }),

  /**
   * base64文字列をファイルに変換します。
   *
   * @param base64 - 変換するbase64文字列
   * @param filename - 生成するファイル名
   * @param options - FilePropertyBag オプション
   * @returns Fileインスタンスを返す
   */
  fromBase64: (base64: string, filename = 'file', options?: FilePropertyBag): File => new File([Buffer.from(base64, 'base64')], filename, options),

  /**
   * PDFファイルをブラウザでプレビューします。
   *
   * @param pdf - プレビューするPDFファイル
   */
  previewPdf: (pdf: File) => {
    // @ts-ignore
    const url = window.URL.createObjectURL(pdf);
    const a = document.createElement('a');
    a.setAttribute('target', '_blank');
    a.href = url;
    a.click();
  },

  /**
   * リクエストボディが指定サイズを超えているか確認します。
   *
   * @param body - チェック対象のオブジェクト
   * @param maxSizeMB - 上限サイズ（MB）
   * @returns trueならサイズオーバー
   */
  isTooLarge: (body: unknown, maxSizeMB = 70): boolean => {
    const jsonStr = JSON.stringify(body);
    const byteSize = new TextEncoder().encode(jsonStr).length;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    return byteSize > maxSizeBytes;
  },

  /**
   * PDFファイルが有効かどうかを確認する。
   *
   * @param pdfFile - PDFファイル
   * @returns エラーメッセージ
   */
  validateDocumentFile: async (pdfFile: File) => {
    if (!pdfFile.size) return PDF_ZERO_BYTE_ERROR;

    let file: any = null;

    try {
      const buffer = await pdfFile.arrayBuffer();

      file = await PDFJS.getDocument({
        data: buffer,
        cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS.version}/cmaps/`,
        cMapPacked: true,
        stopAtErrors: true,
      }).promise;

      const pages = Array.from({ length: file.numPages }, (_, idx) => idx + 1);
      // eslint-disable-next-line no-restricted-syntax
      for (const i of pages) {
        // eslint-disable-next-line no-await-in-loop
        const page = await file.getPage(i);

        // eslint-disable-next-line no-await-in-loop
        await page.getTextContent();

        page.cleanup();
      }

      return '';
    } catch (error: any) {
      if (error?.name === 'PasswordException') {
        return '';
      }

      if (error.message === PDFJS_EMPTY_FILE_ERROR) {
        return PDF_ZERO_BYTE_ERROR;
      }

      return PDF_CORRUPTED_ERROR;
    } finally {
      if (file) {
        await file.destroy();
      }
    }
  },

  /**
  * PDFエラーメッセージを取得
  *
  * @param error - validateDocumentFileのエラーメッセージ
  * @returns エラーメッセージ
  */
  getPdfDisplayErrorMessage: (error: string) => {
    if (!error) return '';
    if (error === PDF_PASSWORD_PROTECTED_ERROR) return 'プレビューを表示できません。';
    return 'このファイルは破損しています。';
  },
};
